import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MainLayoutWithoutFooter from "../components/MainLayoutWithoutFooter";
import { callAPI } from "../utils/apiUtils";
import { apiUrls } from "../utils/apiUrls";
import { toast } from "react-toastify";
import "../styles/customerProfile.css";

const ORDER_STATUS_LABELS = {
  "-1": "Cancelled",
  0: "Inactive",
  1: "Active",
  2: "Pending",
  3: "Placed",
  4: "Cooking",
  5: "Ready for Delivery",
  6: "Out for Delivery",
  7: "Completed",
  8: "Rejected",
};

const RESERVATION_STATUS_LABELS = {
  0: "Rejected",
  1: "Pending",
  2: "Accepted",
  3: "Completed",
  4: "No Show",
};

const CustomerProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await callAPI(apiUrls.POS_CUSTOMER_PROFILE, { id }, "GET");
        setProfile(res.data?.data || null);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load customer profile");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [id]);

  if (loading) {
    return (
      <MainLayoutWithoutFooter>
        <div className="pageContent customersPage pb-3">
          <div className="container">
            <p>Loading customer profile...</p>
          </div>
        </div>
      </MainLayoutWithoutFooter>
    );
  }

  if (!profile) {
    return (
      <MainLayoutWithoutFooter>
        <div className="pageContent customersPage pb-3">
          <div className="container">
            <p>Customer not found.</p>
          </div>
        </div>
      </MainLayoutWithoutFooter>
    );
  }

  const { customer, orders, channelBreakdown, trends, reservations, favourites } = profile;

  return (
    <MainLayoutWithoutFooter>
      <div className="pageContent customersPage pb-3">
        <div className="container">
          <div className="sbRow mb-3">
            <div className="d-flex align-items-center gap-2">
              <button type="button" className="btn iconBtn" onClick={() => navigate("/customer")}>
                ← Back
              </button>
              <h2 className="lgTitle mb-0">{customer.name}</h2>
            </div>
          </div>

          <div className="cardStyle p-3 mb-3">
            <div className="d-flex flex-wrap gap-4" style={{ fontSize: "14px" }}>
              <span>Email: <strong>{customer.email || "--"}</strong></span>
              <span>Mobile: <strong>{customer.phone_number || "--"}</strong></span>
              <span>Subscribed: <strong>{customer.subscribed ? "Yes" : "No"}</strong></span>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-3 mb-3">
            <div className="cardStyle p-3 customerStatCard">
              <div className="customerStatLabel">Total Orders</div>
              <div className="customerStatValue">{trends.orderCount}</div>
            </div>
            <div className="cardStyle p-3 customerStatCard">
              <div className="customerStatLabel">Total Spend</div>
              <div className="customerStatValue">${Number(trends.totalSpend || 0).toFixed(2)}</div>
            </div>
            <div className="cardStyle p-3 customerStatCard">
              <div className="customerStatLabel">Avg Order Value</div>
              <div className="customerStatValue">${Number(trends.avgOrderValue || 0).toFixed(2)}</div>
            </div>
            <div className="cardStyle p-3 customerStatCard">
              <div className="customerStatLabel">First Order</div>
              <div className="customerStatValue" style={{ fontSize: "14px" }}>
                {trends.firstOrderAt ? new Date(trends.firstOrderAt).toLocaleDateString() : "--"}
              </div>
            </div>
            <div className="cardStyle p-3 customerStatCard">
              <div className="customerStatLabel">Last Order</div>
              <div className="customerStatValue" style={{ fontSize: "14px" }}>
                {trends.lastOrderAt ? new Date(trends.lastOrderAt).toLocaleDateString() : "--"}
              </div>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-lg-6 mb-3">
              <div className="cardStyle p-3 h-100">
                <h5 className="mb-3">Orders by Channel</h5>
                {channelBreakdown.length === 0 ? (
                  <p style={{ opacity: 0.7 }}>No orders yet.</p>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {channelBreakdown.map((c) => (
                      <div key={c.type} className="d-flex justify-content-between" style={{ fontSize: "13px" }}>
                        <span>{c.type}</span>
                        <span>{c.orderCount} orders — ${Number(c.totalAmount || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="col-lg-6 mb-3">
              <div className="cardStyle p-3 h-100">
                <h5 className="mb-3">Favourites</h5>
                {favourites.length === 0 ? (
                  <p style={{ opacity: 0.7 }}>Not enough order history yet.</p>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {favourites.map((f) => (
                      <div key={f.itemname} className="d-flex justify-content-between" style={{ fontSize: "13px" }}>
                        <span>{f.itemname}</span>
                        <span>{f.quantity}x ordered ({f.timesOrdered} orders)</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="cardStyle mb-3">
            <div className="p-3">
              <h5 className="mb-0">Order History</h5>
            </div>
            <div className="table-responsive">
              <table className="table tableStyle2">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Source</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length ? (
                    orders.map((o) => (
                      <tr key={o.id}>
                        <td>{o.date ? new Date(o.date).toLocaleString() : "--"}</td>
                        <td>{o.type}</td>
                        <td>{o.source || "--"}</td>
                        <td>${Number(o.amount || 0).toFixed(2)}</td>
                        <td>{ORDER_STATUS_LABELS[o.status] ?? o.status}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">No orders yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="cardStyle mb-3">
            <div className="p-3">
              <h5 className="mb-0">Booking History</h5>
            </div>
            <div className="table-responsive">
              <table className="table tableStyle2">
                <thead>
                  <tr>
                    <th>Date Time</th>
                    <th>Event</th>
                    <th>Guests</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.length ? (
                    reservations.map((r) => (
                      <tr key={r.id}>
                        <td>{r.date} {r.time}</td>
                        <td>{r.event || "--"}</td>
                        <td>{r.guests}</td>
                        <td>{RESERVATION_STATUS_LABELS[r.status] ?? r.status}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">No bookings yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </MainLayoutWithoutFooter>
  );
};

export default CustomerProfilePage;
